/**
 * scripts-components-recently-viewed.js
 * -----------------------------------------------------------------------
 * Two independent, progressively-enhanced behaviors for
 * sections/product-recommendations.liquid:
 *
 *  1. Related products: `recommendations.performed` is only populated
 *     when the SECTION ITSELF is requested through Shopify's
 *     recommendations endpoint (not on a normal page load), so this
 *     fetches that endpoint and swaps in the returned markup — the
 *     standard progressive-enhancement pattern for this Shopify feature.
 *
 *  2. Recently viewed (architecture-level, per spec): records the
 *     current product's handle to `localStorage` and renders a lightweight
 *     card for every other previously-viewed handle by fetching each
 *     product's public `.js` endpoint. Intentionally minimal markup
 *     (image/title/price/link) rather than a full product-card clone,
 *     since this is explicitly scoped as "architecture" rather than a
 *     pixel-identical card.
 * ----------------------------------------------------------------------- */
(function () {
  'use strict';

  var formatMoney = (window.AniMarket && window.AniMarket.formatMoney) || function (cents) {
    return '$' + (cents / 100).toFixed(2);
  };

  var STORAGE_KEY = 'am:recently-viewed';
  var MAX_ENTRIES = 12;

  function readHistory() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (error) {
      return [];
    }
  }

  function writeHistory(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX_ENTRIES)));
    } catch (error) {
      // Ignore storage errors (private browsing, quota, etc.).
    }
  }

  function recordCurrentProduct(handle) {
    if (!handle) return;
    var history = readHistory().filter(function (item) {
      return item !== handle;
    });
    history.unshift(handle);
    writeHistory(history);
  }

  function renderRecentlyViewed(root) {
    var container = root.querySelector('[data-recently-viewed]');
    var grid = root.querySelector('[data-recently-viewed-grid]');
    var headingSlot = root.querySelector('[data-recently-viewed-heading-slot]');
    if (!container || !grid) return;

    var currentHandle = root.getAttribute('data-product-handle');
    var handles = readHistory().filter(function (handle) {
      return handle !== currentHandle;
    });
    if (handles.length === 0) return;

    Promise.all(
      handles.slice(0, 4).map(function (handle) {
        return fetch('/products/' + handle + '.js')
          .then(function (response) {
            return response.ok ? response.json() : null;
          })
          .catch(function () {
            return null;
          });
      })
    ).then(function (products) {
      var validProducts = products.filter(Boolean);
      if (validProducts.length === 0) return;

      if (headingSlot) {
        var heading = document.createElement('h2');
        heading.className = 'section-heading__title';
        heading.textContent = container.getAttribute('data-recently-viewed-heading');
        headingSlot.appendChild(heading);
      }

      validProducts.forEach(function (product) {
        var image = product.featured_image;
        var card = document.createElement('a');
        card.className = 'card card--product product-card';
        card.href = product.url;
        card.innerHTML =
          '<div class="card__media">' +
          (image ? '<img src="' + image + '" alt="" loading="lazy" width="600" height="750">' : '') +
          '</div>' +
          '<div class="card__body">' +
          '<p class="card__title"></p>' +
          '<p class="card__price"></p>' +
          '</div>';
        card.querySelector('.card__title').textContent = product.title;
        card.querySelector('.card__price').textContent = product.price_max
          ? formatMoney(product.price_max)
          : '';
        grid.appendChild(card);
      });

      container.hidden = false;
    });
  }

  function fetchRelatedProducts(root) {
    var url = root.getAttribute('data-url');
    var productId = root.getAttribute('data-product-id');
    var target = root.querySelector('[data-recommendations-target]');
    if (!url || !productId || !target) return;

    fetch(url)
      .then(function (response) {
        return response.ok ? response.text() : null;
      })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var updated = doc.querySelector('[data-recommendations-target]');
        if (updated && updated.innerHTML.trim() !== '') {
          target.innerHTML = updated.innerHTML;
        }
      })
      .catch(function () {});
  }

  document.querySelectorAll('[data-recommendations-root]').forEach(function (root) {
    recordCurrentProduct(root.getAttribute('data-product-handle'));
    fetchRelatedProducts(root);
    renderRecentlyViewed(root);
  });
})();
